import { getIso8601Date } from '@shared/utils'
import { differenceInMonths, differenceInYears, format, parse, startOfMonth } from 'date-fns'
import { ChangeEvent } from 'react'

const FORM_DATE_FORMAT = 'd.M.yyyy'
const MILLIS_IN_YEAR = 365 * 24 * 60 * 60 * 1000

export interface Month {
  index: number
  name: string
}

export const getMonth = (index: number, locale: string): Month => {
  const formatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(locale, { month: 'long' })
  const year = new Date().getFullYear()
  let name: string
  try {
    name = formatter.format(new Date(year, index))
  } catch (_e) {
    name = 'Unknown month'
  }
  return { index, name }
}

export const getMonths = (
  locale: string,
  excludedMonths: number[] = [],
  excludeAlsoConsecutiveMonths = true,
): Month[] => {
  const months = Array.from({ length: 12 }, (_x, i) => i)
  if (excludeAlsoConsecutiveMonths) {
    excludedMonths.forEach((month) => {
      excludedMonths.push(month - 1 === -1 ? 11 : month - 1)
      excludedMonths.push(month + 1 === 12 ? 0 : month + 1)
    })
  }
  return months.filter((month) => excludedMonths.indexOf(month) === -1).map((index) => getMonth(index, locale))
}

export const formDateStringToDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString) {
    return null
  }
  const date = parse(dateString, FORM_DATE_FORMAT, new Date())
  if (isNaN(date.getTime())) {
    return null
  }
  return date
}

/**
 * Converts Date to FE date string
 */
export const dateToFormDateString = (date: Date): string => {
  return format(date, FORM_DATE_FORMAT)
}

/**
 * Converts BFF date string to FE date string 2020-01-21 -> 21.1.2020
 */
export const bffDateStringToFormDateString = (dateString?: string): string | undefined => {
  return dateString ? dateToFormDateString(parse(dateString, 'yyyy-MM-dd', new Date())) : undefined
}

/**
 * Converts FE form date string to bff date string: 21.01.2020 -> 2020-01-21
 */
export const formDateStringToBffDateString = (dateString?: string): string | undefined => {
  const date = formDateStringToDate(dateString)
  return date ? getIso8601Date(date) : undefined
}

export const monthAndYearStringToDate = (monthAndYear: string): Date => {
  return parse(monthAndYear.replaceAll(' ', ''), 'MM/yyyy', startOfMonth(new Date()))
}

export const getDateDifferenceInFullYears = (date1: Date, date2: Date): number => {
  return Math.floor(Math.abs((date1.getTime() - date2.getTime()) / MILLIS_IN_YEAR))
}

export function onDateInputChange(
  event: ChangeEvent<HTMLInputElement>,
  previousValue: string | undefined | null,
  wrapperSetValue: (val: string) => void,
  method?: (splitValue: string[]) => void,
): void {
  const newValue = event.target.value
  const trimmedValue = newValue.trim()

  if (/^[0-1]\d\/\d{4}$/.test(trimmedValue)) {
    // user pasted 02/2000 (no spaces, valid date or not)
    const split = trimmedValue.split('/')
    wrapperSetValue(`${split[0]}/${split[1]}`)
  } else if (/^\d{2}$/.test(trimmedValue) && trimmedValue.length === 2) {
    // user pasted two digit 02
    wrapperSetValue(`${trimmedValue}/`)
  } else if (/^\d{3,}$/.test(trimmedValue)) {
    // user pasted 022000 or any number with more than 3 digits (valid date or not)
    wrapperSetValue(`${trimmedValue.substring(0, 2)}/${trimmedValue.substring(2, 6)}`)
  } else if (/^[2-9]$/.test(newValue)) {
    // Single-digit February to September
    wrapperSetValue(`0${newValue}/`)
  } else if (/^1[\s/]$/.test(newValue)) {
    // Single-digit January special cases
    wrapperSetValue(`01/`)
  } else if (previousValue?.length === 1 && /^(0[1-9]|10|11|12)$/.test(newValue)) {
    // Handle double digits
    wrapperSetValue(`${newValue}/`)
  }

  if (method) {
    const splitValue = newValue.split('/')

    method(splitValue)
  }
}

export function onFullDateInputChange(
  event: ChangeEvent<HTMLInputElement>,
  previousValue: string | undefined | null,
  wrapperSetValue: (val: string) => void,
): void {
  const newValue = event.target.value
  const trimmedValue = newValue.trim()

  if (/^[0-3]\d\.[0-1]\d\.\d{4}$/.test(trimmedValue)) {
    // user pasted 01.02.2000 (no spaces, valid date or not)
    const split = trimmedValue.split('.')
    wrapperSetValue(`${Number(split[0])}.${Number(split[1])}.${split[2]}`)
  } else if (/^([0-3]\d\.\d{1,2}\.\d{4}|\d{1,2}\.[0-1]\d\.\d{4}|\d{1,2}\.\d{1,2}\.\d{4})$/.test(trimmedValue)) {
    // user pasted date or month with two digits
    const split = trimmedValue.split('.')
    const ensureSingleDigitMonth = split[1] && /^[1-9]$/.test(split[1]) ? split[1] : Number(split[1]).toString()
    const ensureSingleDigitDay = split[0] && /^[1-9]$/.test(split[0]) ? split[0] : Number(split[0]).toString()
    wrapperSetValue(`${ensureSingleDigitDay}.${ensureSingleDigitMonth}.${split[2]}`)
  } else if (/^\d{5,}$/.test(trimmedValue)) {
    // user pasted 01022000 or any number with more than 5 digits (valid date or not)
    wrapperSetValue(
      `${Number(trimmedValue.substring(0, 2))}.${Number(trimmedValue.substring(2, 4))}.${trimmedValue.substring(4, 8)}`,
    )
  } else if (/^\d{4,}$/.test(trimmedValue)) {
    // user pasted 0112 or any number with 4 digits (valid date or not)
    wrapperSetValue(`${Number(trimmedValue.substring(0, 2))}.${Number(trimmedValue.substring(2, 4))}.`)
  } else if (/^\d{2,}$/.test(trimmedValue)) {
    // user pasted 010 or any number with 2 digits (valid date or not)
    const secondValue = Number(trimmedValue.substring(2, 3)).toString()
    wrapperSetValue(
      `${Number(trimmedValue.substring(0, 2))}.${secondValue === '0' ? '' : secondValue}${Number(secondValue) > 1 ? '.' : ''}`,
    )
  } else if (/^(0[1-9]|[1-3]\d|[4-9])$/.test(newValue)) {
    // Two-digit day
    wrapperSetValue(`${Number(newValue).toString()}.`)
  } else if (/^(0[1-9]|[1-3]\d|[1-9])\.(0[1-9]|1[0-2]|[2-9])$/.test(newValue.replaceAll(' ', ''))) {
    // Tow-digit day and month
    const split = trimmedValue.split('.')
    wrapperSetValue(`${Number(split[0]).toString()}.${Number(split[1])}.`)
  } else if (
    (previousValue?.length === 1 &&
      (/^([1-9]|[1-2]\d|3[0-1])$/.test(newValue) || /^([1-9]|[1-2]\d|3[0-1])\.(1[0-2])$/.test(newValue))) ||
    /^([1-9]|[1-2]\d|3[0-1])\.1[\s/]$/.test(newValue)
  ) {
    // Handle double digits
    wrapperSetValue(`${newValue}.`)
  }

  if (/^\d{2}$/.test(trimmedValue) && trimmedValue.length === 2) {
    // user pasted two digit 02
    wrapperSetValue(`${Number(trimmedValue)}.`)
  }

  if (trimmedValue.endsWith(',')) {
    wrapperSetValue(trimmedValue.replaceAll(',', '.'))
  }
}

export function onDateInputKeyDown(
  e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  wrapperSetValue: (val: string) => void,
): void {
  const { value } = e.currentTarget
  if (e.key === '/' && value.includes('/')) {
    // prevent extra slashes
    e.preventDefault()
  } else if (value === '1') {
    if (e.key === 'Tab' || e.key === 'ArrowRight') {
      e.preventDefault()
      wrapperSetValue(`0${value}/`)
    }
  } else if (/^(0[1-9]|10|11|12)\s\/\s$/.test(value) || value.endsWith('/')) {
    if (e.key === 'Backspace') {
      // prevent unwanted onDateInputChange 'additions' when removing characters
      wrapperSetValue('')
    }
  }
}

export function onFullDateInputKeyDown(
  e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  wrapperSetValue: (val: string) => void,
): void {
  const { value, selectionStart, selectionEnd } = e.currentTarget

  const isDateComplete = /^\d{1,2}\.\d{1,2}\.\d{4}$/.test(value)
  const hasSelection = selectionStart !== selectionEnd
  const isAtEnd = selectionStart === value.length

  if (/^(\d|[a-zA-Z])$/.test(e.key) && isDateComplete && isAtEnd && !hasSelection) {
    // prevent extra chars only when date is complete AND cursor is at the end
    e.preventDefault()
  }

  if ((e.key === '.' || e.key === ',') && (/^(?=[^.]*\.[^.]*\.[^.]*$).*/.test(value) || value.endsWith('.'))) {
    // prevent extra dots
    e.preventDefault()
  } else if (value === '1') {
    if (e.key === 'Tab' || e.key === 'ArrowRight') {
      e.preventDefault()
      wrapperSetValue(`${value}.`)
    }
  } else if (value.endsWith('.')) {
    if (e.key === 'Backspace') {
      // remove dot and next digit
      wrapperSetValue(value.slice(0, -1).toString())
    }
  }
}

/**
 * @param monthAndYear - MM/YYYY
 * @param yearLimit - years
 * **/
export const isLessThanXYearsAgo = (monthAndYear: string, yearLimit: number): boolean => {
  const monthAndYearAsDate = monthAndYearStringToDate(monthAndYear)
  return differenceInYears(startOfMonth(new Date()), monthAndYearAsDate) < yearLimit
}

/**
 * @param monthAndYear - MM/YYYY
 * @param monthLimit - months
 * **/
export const isLessThanXMonthsAgo = (monthAndYear: string, monthLimit: number): boolean => {
  const monthAndYearAsDate = monthAndYearStringToDate(monthAndYear)
  return differenceInMonths(startOfMonth(new Date()), monthAndYearAsDate) < monthLimit
}
